import type { Account, MarginMode, PendingOrder, Position } from '../types';

interface SubAccountRow {
  sub_account_id: string;
  description: string;
}

interface FutureRow {
  symbol: string;
  index_price: number;
}

interface FundingRateRow {
  symbol: string;
  sum_unitary_funding: number;
}

interface PositionRow {
  account_id: string;
  symbol: string;
  margin_mode: MarginMode;
  position_qty: number;
  cost_position: number;
  last_sum_unitary_funding: number;
  average_open_price: number;
  mark_price: number;
}

interface HoldingRow {
  account_id: string;
  token: string;
  holding: number;
  frozen: number;
  updated_time?: number;
}

interface OrderRow {
  order_id: number;
  account_id?: string;
  symbol: string;
  side: string;
  price?: number;
  quantity?: number;
  type: string;
}

interface AlgoOrderRow {
  algo_order_id: number;
  account_id?: string;
  symbol: string;
  side: string;
  trigger_price?: number;
  quantity?: number;
}

interface BuildAccountsInput {
  authAccountId: string;
  subAccounts: SubAccountRow[];
  futures: FutureRow[];
  fundingRates: FundingRateRow[];
  positions: PositionRow[];
  holdings: HoldingRow[];
  orders: OrderRow[];
  algoOrders: AlgoOrderRow[];
}

function buildIndexPriceMap(futures: FutureRow[]): Map<string, number> {
  const indexPrices = new Map<string, number>();
  for (const future of futures) {
    indexPrices.set(future.symbol, future.index_price);
    const match = future.symbol.match(/^PERP_(.+)_USDC$/);
    if (match) {
      indexPrices.set(match[1], future.index_price);
    }
  }
  return indexPrices;
}

function buildFundingMap(fundingRates: FundingRateRow[]): Map<string, number> {
  const sumUnitaryFundingMap = new Map<string, number>();
  for (const rate of fundingRates) {
    sumUnitaryFundingMap.set(rate.symbol, rate.sum_unitary_funding);
  }
  return sumUnitaryFundingMap;
}

function calcUnsettledPnl(position: PositionRow, fundingMap: Map<string, number>): number {
  const sumUnitaryFunding = fundingMap.get(position.symbol) ?? 0;
  return position.position_qty * position.mark_price
    - position.cost_position
    - position.position_qty * (sumUnitaryFunding - position.last_sum_unitary_funding);
}

function buildPendingOrdersByAccount(
  authAccountId: string,
  orders: OrderRow[],
  algoOrders: AlgoOrderRow[],
): Map<string, PendingOrder[]> {
  const byAccount = new Map<string, PendingOrder[]>();
  const append = (accountId: string | undefined, order: PendingOrder) => {
    const targetAccountId = accountId ?? authAccountId;
    const list = byAccount.get(targetAccountId) ?? [];
    list.push(order);
    byAccount.set(targetAccountId, list);
  };

  for (const order of orders) {
    append(order.account_id, {
      id: String(order.order_id),
      symbol: order.symbol,
      side: order.side === 'BUY' ? ('Long' as const) : ('Short' as const),
      price: order.price ?? 0,
      quantity: order.quantity ?? 0,
      type: order.type === 'LIMIT' ? ('Limit' as const) : ('Stop' as const),
    });
  }

  for (const order of algoOrders) {
    append(order.account_id, {
      id: String(order.algo_order_id),
      symbol: order.symbol,
      side: order.side === 'BUY' ? ('Long' as const) : ('Short' as const),
      price: order.trigger_price ?? 0,
      quantity: order.quantity ?? 0,
      type: 'Stop',
    });
  }

  return byAccount;
}

function buildGroupedHoldings(holdings: HoldingRow[]): Map<string, HoldingRow[]> {
  const grouped = new Map<string, HoldingRow[]>();
  for (const holding of holdings) {
    const list = grouped.get(holding.account_id) ?? [];
    list.push(holding);
    grouped.set(holding.account_id, list);
  }
  return grouped;
}

function buildGroupedPositions(
  positions: PositionRow[],
  indexPrices: Map<string, number>,
  fundingMap: Map<string, number>,
): { positionsByAccount: Map<string, Position[]>; unsettledPnlByAccount: Map<string, number> } {
  const positionsByAccount = new Map<string, Position[]>();
  const unsettledPnlByAccount = new Map<string, number>();

  for (const position of positions) {
    const pnl = calcUnsettledPnl(position, fundingMap);
    unsettledPnlByAccount.set(
      position.account_id,
      (unsettledPnlByAccount.get(position.account_id) ?? 0) + pnl,
    );

    if (position.position_qty === 0) {
      continue;
    }

    const list = positionsByAccount.get(position.account_id) ?? [];
    list.push({
      accountId: position.account_id,
      symbol: position.symbol,
      marginMode: position.margin_mode,
      side: position.position_qty > 0 ? 'Long' : 'Short',
      quantity: Math.abs(position.position_qty),
      avgOpenPrice: position.average_open_price,
      markPrice: indexPrices.get(position.symbol) ?? position.mark_price,
      unrealizedPnl: pnl,
      unrealizedRoi: position.average_open_price
        ? pnl / (Math.abs(position.position_qty) * position.average_open_price)
        : 0,
    });
    positionsByAccount.set(position.account_id, list);
  }

  return { positionsByAccount, unsettledPnlByAccount };
}

export function buildAccountsFromApiData(input: BuildAccountsInput): Account[] {
  const accountRows = [
    { accountId: input.authAccountId, name: 'Main account' },
    ...input.subAccounts.map((account) => ({
      accountId: account.sub_account_id,
      name: account.description || account.sub_account_id.slice(0, 10),
    })),
  ];
  const indexPrices = buildIndexPriceMap(input.futures);
  const fundingMap = buildFundingMap(input.fundingRates);
  const holdingsByAccount = buildGroupedHoldings(input.holdings);
  const { positionsByAccount, unsettledPnlByAccount } = buildGroupedPositions(
    input.positions,
    indexPrices,
    fundingMap,
  );
  const pendingOrdersByAccount = buildPendingOrdersByAccount(input.authAccountId, input.orders, input.algoOrders);

  const accounts = accountRows.map((account, index) => {
    const holdings = holdingsByAccount.get(account.accountId) ?? [];
    const positions = positionsByAccount.get(account.accountId) ?? [];
    const unsettledPnl = unsettledPnlByAccount.get(account.accountId) ?? 0;
    const holdingsValue = holdings.reduce(
      (sum, holding) => sum + holding.holding * (indexPrices.get(holding.token) ?? 1),
      0,
    );
    const usdcBalance = holdings.find((holding) => holding.token === 'USDC')?.holding ?? 0;

    return {
      accountId: account.accountId,
      name: account.name,
      type: index === 0 ? 'main' : 'sub',
      positions,
      pendingOrders: pendingOrdersByAccount.get(account.accountId) ?? [],
      holdings: holdings.map((holding) => ({
        token: holding.token,
        holding: holding.holding,
        frozen: holding.frozen,
        updatedTime: holding.updated_time,
      })),
      totalAssets: holdingsValue + unsettledPnl,
      usdcBalance,
      unsettledPnl,
    } satisfies Account;
  });

  if (accounts.length > 0) {
    return accounts;
  }

  const unsettledPnl = [...unsettledPnlByAccount.values()].reduce((sum, value) => sum + value, 0);
  const usdcBalance = input.holdings.find((holding) => holding.token === 'USDC')?.holding ?? 0;
  const holdingsValue = input.holdings.reduce(
    (sum, holding) => sum + holding.holding * (indexPrices.get(holding.token) ?? 1),
    0,
  );

  return [{
    accountId: input.authAccountId,
    name: 'Main account',
    type: 'main',
    positions: [...positionsByAccount.values()].flat(),
    pendingOrders: pendingOrdersByAccount.get(input.authAccountId) ?? [],
    holdings: input.holdings.map((holding) => ({
      token: holding.token,
      holding: holding.holding,
      frozen: holding.frozen,
      updatedTime: holding.updated_time,
    })),
    totalAssets: holdingsValue + unsettledPnl,
    usdcBalance,
    unsettledPnl,
  }];
}
